<template>
  <bm-form
    v-if="internalValue !== null"
    ref="form"
    v-model="valid"
    data-qa="edit_box"
    @submit="validateAndSave"
  >
    <bm-edit-card
      v-model="internalEditing"
      :label="label"
      :read-only="readOnly"
      v-bind="$attrs"
    >
      <slot :value="internalValue" :editing="internalEditing"> </slot>

      <template #controls>
        <bm-btn
          v-if="closable"
          button-type="secondary"
          data-qa="edit_box_cancel"
          @click="reset()"
          >{{ $m.cancel }}</bm-btn
        >
        <v-spacer />
        <slot name="additional-controls-right" :value="internalValue" />
        <bm-btn
          button-type="primary"
          :disabled="
            (formValidation && !valid) ||
            (!allowNonDirtySaving && !dirtyChecker.isDirty())
          "
          data-qa="edit_box_save"
          @click="validateAndSave()"
          >{{ $m.save }}</bm-btn
        >
      </template>
    </bm-edit-card>
  </bm-form>
</template>

<script setup lang="ts">
import BmEditCard from "@/components/controls/card/BmEditCard.vue";
import type { IModelObject } from "@/app/services/modelObject";
import { DirtyChecker } from "@/app/services/dirtyChecker";
import appStore from "@/store/modules/appStore";
import { watch, nextTick, ref } from "vue";
import {
  makeDeepRef,
  makeRef,
  wrapLoading,
} from "@/components/composables/componentTools";
import type { BmFormInterface } from "@/components/controls/BmFormTypes";

/**
 * A card with two states - readonly and editing. Wraps an IModelObject to provide dirty, edit, save, cancel
 * functionality in a consistent way. It also calls validate automatically before save.
 *
 * form-validation: Property to disable form validation if required.
 *
 * Emits 'input' event when a user clicks save (as well as calling the function pointed at by the 'save' property'.
 * Emits 'changed' when it's internal state changes (before the user clicks 'save').
 */

interface BmEditBoxProps {
  label?: string;
  readOnly?: boolean;
  formValidation?: boolean;
  editing?: boolean;
  isEditingAndDirty?: boolean;
  value: IModelObject;
  save?: (obj: IModelObject) => void;
  closable?: boolean;
  allowNonDirtySaving?: boolean;
}

const props = withDefaults(defineProps<BmEditBoxProps>(), {
  label: "",
  readOnly: false,
  formValidation: false,
  editing: false,
  isEditingAndDirty: false,
  save: () => () => (obj: IModelObject) => {},
  closable: true,
  allowNonDirtySaving: false,
});

interface BmEditBoxEvents {
  (event: "input", value: IModelObject | null): void;
  (event: "changed", value: IModelObject | null): void;
  (event: "edit-change", value: Boolean): void;
  (event: "update:is-editing-and-dirty", value: Boolean): void;
}

const emit = defineEmits<BmEditBoxEvents>();

const internalValue = makeDeepRef<IModelObject | null>(null);
const dirtyChecker = new DirtyChecker<IModelObject>();
const internalEditing = makeRef(false);
const valid = makeRef(true);
const form = ref<BmFormInterface | null>(null);

const valueWatch = () => {
  reset();
};

const internalValueWatch = (internalValue: IModelObject | null) => {
  emit("changed", internalValue);
  isEditingAndDirtyCalculate();
};

const editingWatch = (newVal: boolean) => {
  internalEditing.value = newVal;
};

const internalEditingWatch = (newVal: boolean) => {
  emit("edit-change", newVal);
  nextTick(() => {
    (form.value as any).focus();
  });
  isEditingAndDirtyCalculate();
};

const isEditingAndDirtyCalculate = () => {
  emit(
    "update:is-editing-and-dirty",
    internalEditing && dirtyChecker.isDirty()
  );
};

const reset = () => {
  internalEditing.value = false;
  const clone = props.value.clone();
  internalValue.value = clone;
  dirtyChecker.setItem(clone);
};

const validateAndSave = async () => {
  await wrapLoading(async () => {
    if (!internalValue.value) {
      return;
    }

    if (props.formValidation && form.value && !form.value.validate()) {
      return false;
    }

    // Validate model object
    let errors = [...internalValue.value.validate()];
    if (errors.length) {
      appStore.showError(errors.join(", "));
      return false;
    }

    await props.save(internalValue.value);

    emit("input", internalValue.value);
    internalEditing.value = false;
  });
};

watch(() => props.value, valueWatch, { immediate: true });
watch(internalValue, internalValueWatch, { deep: true });
watch(() => props.editing, editingWatch, { immediate: true });
watch(internalEditing, internalEditingWatch);
</script>
